import React from 'react'
import { usePracticeInterviewing } from './PracticeInterviewingProvider'
import { Button } from '~/lib/ui/button'
import { useTranslation } from '../../hooks/useTranslation'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'
import Spinner from '../shared/Spinner'
import AylaRatingFeedback from '../shared/AylaRatingFeedback'

const PracticeInterviewResponses = () => {
  const { uiState, setUiState } = usePracticeInterviewing()
  const { documentId } = uiState

  const { t } = useTranslation('PracticeInterviewResponses')

  const { data, error } = useQuery({
    queryKey: ['interviewResponses', documentId],
    queryFn: () =>
      apiGet(`/interview/practice_interviewing/${documentId}.json`),
    enabled: !!documentId,
  })

  const interviewResponses = data?.data?.interviewResponses || []
  const aiInterview = data?.data?.aiInterview || false
  const feedbackRating = data?.data?.feedbackRating
  const starDescriptions = t('star_ratings', {
    defaultValue: [
      'Missed the Mark',
      'Needs Improvement',
      'Pretty Good',
      'Really Helpful',
      'Exactly What I Needed',
    ],
  })

  if (error) {
    console.error('Error fetching interview responses:', error)
    return <div>Error loading interview responses</div>
  }
  return (
    <div className="flex w-full flex-col">
      <h2 className="mb-4">{t('title')}</h2>
      <p className="mb-4">{t('description')}</p>
      {interviewResponses.map((response, idx) => (
        <PracticeInterviewResponse
          key={response.id}
          response={response}
          idx={idx}
        />
      ))}
      {aiInterview && (
        <AylaRatingFeedback
          routes={{
            createFeedback: `/interview/practice_interviewing/${documentId}/feedback`,
          }}
          content={documentId}
          title={t('how_helpful')}
          subtitle={t('your_feedback')}
          starDescriptions={starDescriptions}
          className="sm:w-2/3"
          feedbackRating={feedbackRating}
        />
      )}
      <div className="flex w-full justify-center">
        <Button
          onClick={() => {
            setUiState((prev) => ({
              ...prev,
              showResponses: false,
              activeTab: 'previousPracticeInterviews',
              formIndex: 0,
              aiOrGeneral: true,
            }))
          }}
        >
          {t('back_to_practice_interviews')}
        </Button>
      </div>
    </div>
  )
}

export default PracticeInterviewResponses

const PracticeInterviewResponse = ({ response, idx }) => {
  const { t } = useTranslation('PracticeInterviewResponses')
  const {
    s3Filenames: { video, image },
    id,
    customizedInterviewQuestion,
    generalInterviewQuestion,
    addpipeNamePresent,
  } = response
  const videoUrl = decodeURIComponent(video)
  return (
    <div className="w-full pb-16">
      <p className="mb-4">{t('question_number', { number: idx + 1 })}</p>
      <h2 className="mb-4">
        {customizedInterviewQuestion || generalInterviewQuestion}
      </h2>
      {addpipeNamePresent ? (
        <div
          id={'addpipe_response_' + id}
          className="addpipe_response mx-auto flex w-full justify-center"
        >
          <video
            id={'addpipe_video_' + id}
            poster={decodeURIComponent(image) || ''}
            width="500"
            height="229"
            controls
            src={videoUrl || ''}
          >
            {t('does_not_support')}
          </video>
        </div>
      ) : (
        <>
          <Spinner show={true} className="mx-auto mt-4 w-14" />
          <p className="mt-8 text-center">{t('response_being_generated')}</p>
        </>
      )}
    </div>
  )
}
